import { useEffect, useState } from "react";
import { useCurrentUser, useCentralApi } from "hooks/shared";

type WorkItemAttachmentDownloadResult = [
    boolean
];

const makeAttachmentDownloadUrl = (apibaseurl: string | undefined, attachmentId: string, draftId: number | undefined, draft?: boolean) => draft
    ? `${apibaseurl}/api/v3/drafts/workitems/${draftId}/attachments/${attachmentId}/download`
    : `${apibaseurl}/api/v3/attachments/${attachmentId}/download`;

    const useWorkItemAttachmentDownload: (
        dependenciesLoaded: boolean,
        attachments: { id: string, name: string }[],
        draft?: boolean,
        draftId?: number
    ) => WorkItemAttachmentDownloadResult = (dependenciesLoaded, attachments, draft?, draftId?) => {
        const [loading, setLoading] = useState<boolean>(true);
        const user = useCurrentUser();
    
        useEffect(() => {
            const controller = new AbortController();
            const fetchWorkItemAttachmentDownload = async () => {
                const apibaseurl = process.env.REACT_APP_API_URL;
                if (user && user.id_token && attachments.length > 0) {
                    setLoading(true);
    
                    for (const attachment of attachments) {
                        if (attachment?.id && attachment?.name) {
                            const urlRoot = makeAttachmentDownloadUrl(apibaseurl, attachment.id, draftId, draft);
                            try {
                                const response = await fetch(urlRoot, {
                                    headers: {
                                        Authorization: `Bearer ${user.id_token}`
                                    }
                                });
                    
                                if (!response.ok) {
                                    console.error(`Failed to fetch ${attachment.name}`, response.status);
                                    continue;
                                }
                    
                                const blob = await response.blob();
                                const blobUrl = URL.createObjectURL(blob);
                    
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = attachment.name;
                    
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                    
                                URL.revokeObjectURL(blobUrl);
                            } catch (err) {
                                console.error(`Error downloading ${attachment.name}`, err);
                            }
                        }
                    }
    
                    setLoading(false);
                }
            };
    
            if (dependenciesLoaded) {
                fetchWorkItemAttachmentDownload();
            }
    
            return () => {
                controller.abort();
                setLoading(false);
            };
        }, [dependenciesLoaded, user, attachments]);
    
        return [loading];
    };

export default useWorkItemAttachmentDownload;